/**
 * Helper Class
 */
export class Helper {

	/**
	 * Check if the element is invalid
	 *
	 * @param {HTMLElement} el - The element to check
	 * @throws {Error} - Throws an error if the element is invalid
	 * @return {void}
	 */
	static isInvalidElement = ( el ) => {

		if ( !el || !( el instanceof HTMLElement ) ) {

			throw new Error( 'Invalid element provided' );

		}

	};

	/**
	 * Check if the NodeList is invalid
	 *
	 * @param {NodeListOf<HTMLElement>} elements - The NodeList to check
	 * @throws {Error} - Throws an error if the NodeList is invalid
	 * @return {void}
	 */
	static isInvalidNodeList = ( elements ) => {

		if ( !elements || !( elements instanceof NodeList ) ) {

			throw new Error(
				'Invalid elements provided. Expected NodeListOf<HTMLElement>.'
			);

		}

	};

	/**
	 * Handle error logging
	 *
	 * @param {Error} error - The error object
	 * @param {string} message - The error message
	 * @return {void}
	 */
	static handleError = ( error, message ) => {

		// eslint-disable-next-line no-console
		console.error( message, error );

	};

	/**
	 * Execute a function with error handling
	 *
	 * @param {HTMLElement} el - The element to check
	 * @param {Function} fn - The function to execute
	 * @param {string} [errorMessage] - The optional error message to log
	 * @return {any} - Returns the result of the function or false if an error occurs
	 */
	static executeWithHandling = (
		el,
		fn,
		errorMessage = 'Error executing function on element:'
	) => {

		try {

			Helper.isInvalidElement( el );
			return fn( el );

		} catch ( error ) {

			Helper.handleError( error, errorMessage );
			return false;

		}

	};

	/**
	 * Validate action for toggleClass method
	 *
	 * @param {string} action - The action to validate
	 * @throws {Error} - Throws an error if the action is invalid
	 * @return {void}
	 */
	static validateAction = ( action ) => {

		if ( action !== 'add' && action !== 'remove' ) {

			throw new Error( 'Invalid action provided. Use "add" or "remove".' );

		}

	};

	/**
	 * Toggle class on element based on action
	 *
	 * @param  {HTMLElement} el
	 * @param  {string} className
	 * @param {string} action - 'add' or 'remove'
	 *
	 * @return {void}
	 */
	static toggleClass = ( el, className, action ) => {

		try {

			Helper.isInvalidElement( el );
			Helper.validateAction( action );
			el.classList.toggle( className, action === 'add' );

		} catch ( error ) {

			Helper.handleError( error, `Error ${action}ing class to element:` );

		}

	};

	/**
	 * Add Class to element if it does not exist on the element already
	 * @param {HTMLElement} el
	 * @param {string} className
	 * @return {void}
	 */
	static addClassToEl = ( el, className ) => {

		Helper.toggleClass( el, className, 'add' );

	};

	/**
	 * Remove Class from element if it exists on the element already
	 *
	 * @param {HTMLElement} el
	 * @param {string} classToRemove
	 * @return {void}
	 */
	static removeClassFromEl = ( el, classToRemove ) => {

		Helper.toggleClass( el, classToRemove, 'remove' );

	};

	/**
	 * Check if element has class
	 *
	 * @param {HTMLElement} el
	 * @param {string} classToCheck
	 * @return {boolean}
	 */
	static hasClass = ( el, classToCheck ) => {

		Helper.isInvalidElement( el );

		return Helper.executeWithHandling(
			el,
			() => el.classList.contains( classToCheck ),
			'Error checking class on element:'
		);

	};

	/**
	 * Remove Class from all elements
	 *
	 * @param {NodeListOf<HTMLElement>} elements
	 * @param {string} classToRemove
	 * @return {void}
	 */
	static removeClassFromAll = ( elements, classToRemove ) => {

		Helper.isInvalidNodeList( elements );

		Array.from( elements ).forEach( ( el ) => {

			Helper.removeClassFromEl( el, classToRemove );

		} );

	};

	/**
	 * Add Class to all elements
	 *
	 * @param {NodeListOf<HTMLElement>} elements
	 * @param {string} classToAdd
	 * @return {void}
	 */
	static addClassToAll = ( elements, classToAdd ) => {

		Helper.isInvalidNodeList( elements );

		Array.from( elements ).forEach( ( el ) => {

			Helper.addClassToEl( el, classToAdd );

		} );

	};

	/**
	 * Check if element has data attribute
	 *
	 * @param {HTMLElement} el - The element to check
	 * @param {string} attrToCheck - The attribute to check for on the element
	 * @return {boolean} - Returns true if the element has the attribute, false if it does not
	 */
	static hasDataAttr = ( el, attrToCheck ) => {

		Helper.isInvalidElement( el );
		return Helper.executeWithHandling(
			el,
			() => el.hasAttribute( attrToCheck ),
			'Error checking data attribute on element:'
		);

	};

	/**
	 * Debounce function to limit the rate at which a function can fire.
	 *
	 * @param {Function} func - The function to debounce.
	 * @param {number} wait - The number of milliseconds to delay.
	 * @return {Function} - Returns the debounced function.
	 */
	static debounce = ( func, wait ) => {

		let timeout;
		return ( ...args ) => {

			/**
			 * Later function to execute the debounced function after the wait time.
			 */
			const later = () => {

				clearTimeout( timeout );
				func.apply( this, args );

			};
			clearTimeout( timeout );
			timeout = setTimeout( later, wait );

		};

	};

}
